// src/app/modules/authentication/presentation/pages/register/register.component.ts
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AppState } from '../../../../../core/application/store/app.state';
import {
  isAuthenticatingSelector,
  loginFailureSelector,
} from '@geekbears/gb-ngrx-auth-basic';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { isNil } from 'lodash-es';
import { ActivatedRoute, Router } from '@angular/router';
import { checkEmailAvailability } from '../../application/register/register.actions';
import { selectChecking, selectAvailable, selectCheckError } from '../../application/register/register.selectors';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnInit {
  /** Form – only email */
  registerForm = this.fb.group({
    email: [
      '',
      [
        Validators.required,
        Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
      ],
    ],
  });

  /** Global auth loading (keeps button disabled while any auth request runs) */
  loading$!: Observable<boolean>;

  /** Email-check specific streams */
  checking$!: Observable<boolean>;
  available$!: Observable<boolean | null>;
  checkError$!: Observable<any>;

  /** Error display – reuse login failure selector (optional) */
  displaError$!: Observable<boolean>;
  errorMessage$!: Observable<string | null>;

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initStreams();
    this.loadQueryParams();
  }

  private initStreams(): void {
    this.loading$ = this.store.select(isAuthenticatingSelector);

    this.checking$ = this.store.select(selectChecking);
    this.available$ = this.store.select(selectAvailable);
    this.checkError$ = this.store.select(selectCheckError);

    // Show any login-related error (you can also show checkError$ separately)
    this.displaError$ = this.store.select(loginFailureSelector).pipe(map(f => !isNil(f)));
    this.errorMessage$ = this.store.select(loginFailureSelector).pipe(getFailureMessageRxOp());
  }

  private loadQueryParams(): void {
    this.route.queryParams
      .pipe(map(p => p))
      .subscribe(p => {
        if (p['email']) {
          const email = p['email'].replaceAll(' ', '+');
          this.emailCtrl.setValue(email);
        }
      });
  }

  get emailCtrl(): FormControl {
    return this.registerForm.get('email') as FormControl;
  }

  emailToLowercase(ev: any): void {
    const lower = ev.target.value.toLocaleLowerCase();
    this.emailCtrl.setValue(lower, { emitEvent: false });
  }

  /** ---------- SUBMIT ---------- */
  onSubmit(): void {
    if (this.registerForm.invalid) return;

    const email = this.emailCtrl.value.trim();
    this.store.dispatch(checkEmailAvailability({ email }));

    // Navigate when email is confirmed
    this.available$
      .pipe(
        tap(available => {
          if (available === true) {
            localStorage.setItem('pendingRegisterEmail', email);
            this.router.navigate(['/authentication/organization-information']);
          }
        })
      )
      .subscribe();
  }
}